import { CopyButton } from '@/components/ui/copy-button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { DataSourceColumn, DataSourceTableDetail } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Link2 } from 'lucide-react';
import { DescriptionEditor } from './DescriptionEditor';
import { SampleValuesCell } from './SampleValuesCell';

interface ColumnTableProps {
  dataSourceId: string;
  detail: DataSourceTableDetail;
}

function formatNumber(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatRange(min: string | null, max: string | null): string {
  if (min === null && max === null) return '—';
  return `${min ?? '∅'} ~ ${max ?? '∅'}`;
}

export function ColumnTable({ dataSourceId, detail }: ColumnTableProps) {
  const qc = useQueryClient();

  const columnMutation = useMutation({
    mutationFn: ({
      columnId,
      description,
    }: {
      columnId: string;
      description: string | null;
    }) => api.dataSourceUpdateColumn(dataSourceId, detail.table.id, columnId, { description }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'data-sources', dataSourceId, 'tables', detail.table.id],
      });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '컬럼 설명 저장에 실패했습니다.');
    },
  });

  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th w="40px">#</Th>
            <Th>컬럼</Th>
            <Th>타입</Th>
            <Th>NULL</Th>
            <Th>통계</Th>
            <Th>샘플</Th>
            <Th>설명</Th>
          </tr>
        </thead>
        <tbody>
          {detail.columns.map((col) => (
            <ColumnRow
              key={col.id}
              col={col}
              onSaveDescription={(description) =>
                columnMutation.mutateAsync({ columnId: col.id, description })
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, w }: { children: React.ReactNode; w?: string }) {
  return (
    <th
      scope="col"
      className="px-3 py-2.5 font-medium text-left"
      style={w ? { width: w } : undefined}
    >
      {children}
    </th>
  );
}

function ColumnRow({
  col,
  onSaveDescription,
}: {
  col: DataSourceColumn;
  onSaveDescription: (next: string | null) => Promise<unknown>;
}) {
  const isRemoved = !!col.removedAt;
  return (
    <tr className={cn('border-t border-border align-top', isRemoved && 'opacity-50')}>
      <td className="px-3 py-3 text-foreground-muted font-mono text-xs">{col.ordinal}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5">
          {col.isPk ? <KeyRound className="size-3.5 text-accent" aria-label="PK" /> : null}
          {col.fkTarget ? (
            <Link2 className="size-3.5 text-foreground-muted" aria-label={`FK → ${col.fkTarget}`} />
          ) : null}
          <span className="group/col inline-flex items-center gap-1">
            <span className="font-medium">{col.columnName}</span>
            <CopyButton
              text={col.columnName}
              label={`${col.columnName} 복사`}
              className="opacity-0 group-hover/col:opacity-100 focus-visible:opacity-100"
            />
          </span>
        </div>
        {col.fkTarget ? (
          <div className="mt-0.5 text-[11px] text-foreground-muted font-mono">→ {col.fkTarget}</div>
        ) : null}
      </td>
      <td className="px-3 py-3 font-mono text-xs text-foreground-muted">{col.dataType}</td>
      <td className="px-3 py-3 text-xs text-foreground-muted">{col.isNullable ? 'YES' : 'NO'}</td>
      <td className="px-3 py-3 text-xs text-foreground-muted leading-relaxed">
        <div>distinct: {formatNumber(col.cardinality)}</div>
        <div>nulls: {formatNumber(col.nullCount)}</div>
        <div className="truncate max-w-[220px]" title={formatRange(col.minValue, col.maxValue)}>
          range: {formatRange(col.minValue, col.maxValue)}
        </div>
      </td>
      <td className="px-3 py-3">
        <SampleValuesCell values={col.sampleValues} />
      </td>
      <td className="px-3 py-3 min-w-[240px]">
        <DescriptionEditor value={col.description} variant="inline" onSave={onSaveDescription} />
        {col.descriptionSource && col.descriptionSource !== 'manual' ? (
          <span
            className={cn(
              'mt-1 inline-flex items-center rounded-sm px-1.5 h-4 text-[9px] uppercase tracking-wider',
              col.descriptionSource === 'llm'
                ? 'bg-accent/15 text-accent'
                : 'bg-surface-muted/60 text-foreground-muted',
            )}
          >
            {col.descriptionSource === 'llm' ? 'LLM 유추' : 'DB 코멘트 원본'}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
